import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import Filter from '../../../../icons/filter.svg';
import { Typography, Popover, Button, Grid, Stack, SelectChangeEvent } from '@mui/material';
import { OwnerSearchRequestDto, OwnerSearchRequestDtoRegistrationStatusEnum, OwnerSearchRequestDtoStatusEnum, OwnerSort, OwnerSortColumnEnum, OwnerSortDirectionEnum } from '@inosio/payments-sdk';

import { useDispatch, useSelector } from 'react-redux';

import { selectAuthState } from '../../../../shared/sagas/auth/authSlice';
import { getFetchingOwnersData } from '../../sagas/ownersSlice';
import { ISelectInputOption } from './OwnersTableHeaderMultiSelectInput';
import OwnersTableHeaderTextInput from './OwnersTableHeaderTextInput';
import OwnersTableHeaderChip from './OwnersTableHeaderChip';
import OwnersTableHeaderMultiSelectInput from './OwnersTableHeaderMultiSelectInput';

const registrationStatusFilterOptions: ISelectInputOption[] = [
  { value: OwnerSearchRequestDtoRegistrationStatusEnum.Registered, label: 'Registered' },
  { value: OwnerSearchRequestDtoRegistrationStatusEnum.Unregistered, label: 'Unregistered' },
];

const statusFilterOptions: ISelectInputOption[] = [
  { value: OwnerSearchRequestDtoStatusEnum.Active, label: 'Active' },
  { value: OwnerSearchRequestDtoStatusEnum.Inactive, label: 'Inactive' },
];

export interface OwnerTableHeaderProps {
  /**
   * Pagination prop to tell the header, how many rows to fetch
   */
  rangeSize: number;
  /**
   * Pagination prop to tell the header the starting point from which we have to fetch the results
   * This is overridden to 0 whenever we update/delete the filters in the header
   */
  startIndex: number;

  /**
   * This function updates the current page of the owners data we are on
   * Useful when filters change and we need to fetch new data
   * @param newValue
   * @returns
   */
  updateCurrentPage: (newValue: number) => void;

  /**
   * This function fetch owners data
   * @returns
   */

  handleSearch: any;

  sortColumn: OwnerSortColumnEnum;

  /**
   * The direction of sorting (asc or desc)
   */
  sortDirection: OwnerSortDirectionEnum;
}

const OwnersFilterTableHeader = (props: OwnerTableHeaderProps) => {
  // State to control the popover visibility
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  // Boolean to check if the popover is open
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  //local filter states

  const [ownerNameFilter, setOwnerNameFilter] = useState('');
  const [ownerIdFilter, setOwnerIdFilter] = useState('');
  const [propertyNameFilter, setPropertyNameFilter] = useState('');
  const [selectedRegistrationStatusFilterOptions, setSelectedRegistrationStatusFilterOptions] = useState<string[]>([]);
  const [selectedStatusFilterOptions, setSelectedStatusFilterOptions] = useState<string[]>([OwnerSearchRequestDtoStatusEnum.Active]);
  //chip states

  const [isOwnerNameFilterChipOpen, setIsOwnerNameFilterChipOpen] = useState(false);
  const [isOwnerIdFilterChipOpen, setIsOwnerIdFilterChipOpen] = useState(false);
  const [isPropertyNameFilterChipOpen, setIsPropertyNameFilterChipOpen] = useState(false);
  const [isSelectedRegistrationStatusChipOpen, setIsSelectedRegistrationStatusChipOpen] = useState(false);
  const [isSelectedStatusChipOpen, setIsSelectedStatusChipOpen] = useState(false);
  const { isSendingInvitationSuccess } = useSelector(selectAuthState);

  useEffect(() => {
    fetchOwners();
  }, [dispatch, props.rangeSize, props.startIndex, props.sortColumn, props.sortDirection]);

  useEffect(() => {
    if (isSendingInvitationSuccess) {
      fetchOwners();
    }
  }, [dispatch, isSendingInvitationSuccess]);

  //handlers

  /**
   * Fetches owners that satisfy our search parameters
   * @param filterOverrides an optional filterOverrides parameter so we can override any property in the payload when needed
   * @param isSilentReload flag that tells the component to not show the spinner while owners are being fetched for better ux in the scenario where we edit the accepted payment methods of a owner and refetch the results
   */
  const fetchOwners = (filterOverrides: Partial<OwnerSearchRequestDto> = {}, isSilentReload?: boolean) => {
    const payload: OwnerSearchRequestDto = {
      RangeSize: props.rangeSize,
      StartIndex: props.startIndex,
      OwnerName: ownerNameFilter || undefined,
      OwnerExternalId: ownerIdFilter || undefined,
      RegistrationStatus: selectedRegistrationStatusFilterOptions.length ? (selectedRegistrationStatusFilterOptions as OwnerSearchRequestDtoRegistrationStatusEnum[]) : undefined,
      PropertyName: propertyNameFilter || undefined,
      Sort: props.sortColumn ? ({ Column: props.sortColumn as OwnerSortColumnEnum, Direction: props.sortDirection as OwnerSortDirectionEnum } as OwnerSort) : undefined,
      Status: selectedStatusFilterOptions.length ? (selectedStatusFilterOptions as OwnerSearchRequestDtoStatusEnum[]) : undefined,
      ...filterOverrides,
    };

    for (const [key] of Object.entries(payload)) {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    }

    dispatch(getFetchingOwnersData({ ...payload, IsSilentReload: isSilentReload }));
  };

  const handleSearch = () => {
    fetchOwners({ StartIndex: 0 });
    props.updateCurrentPage(0);
    !!ownerNameFilter && setIsOwnerNameFilterChipOpen(true);
    !!ownerIdFilter && setIsOwnerIdFilterChipOpen(true);
    !!propertyNameFilter && setIsPropertyNameFilterChipOpen(true);
    !!selectedRegistrationStatusFilterOptions.length && setIsSelectedRegistrationStatusChipOpen(true);
    !!selectedStatusFilterOptions.length && setIsSelectedStatusChipOpen(true);

    handleClose();
  };

  // Function to handle opening the popover
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget); // Set the clicked element as the anchor
  };
  // Function to handle closing the popover
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle changes in the registration status select input in filters
  const handleChangeRegistrationStatusFilter = (event: SelectChangeEvent<string[]>) => {
    setIsSelectedRegistrationStatusChipOpen(false); //if they change the input, remove the chip for better ux
    const { value } = event.target;
    setSelectedRegistrationStatusFilterOptions(typeof value === 'string' ? value.split(',') : value);
  };

  // Handle changes in the status select input in filters
  const handleChangeStatusFilter = (event: SelectChangeEvent<string[]>) => {
    setIsSelectedStatusChipOpen(false); //if they change the input, remove the chip for better ux
    const { value } = event.target;
    setSelectedStatusFilterOptions(typeof value === 'string' ? value.split(',') : value);
  };

  // Handle changes in the owner name input in filters
  const handleChangeOwnerNameFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOwnerNameFilterChipOpen(false); //if they change the input, remove the chip for better ux
    setOwnerNameFilter(event.target.value);
  };

  // Handle changes in the owner id input in filters
  const handleChangeOwnerIdFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOwnerIdFilterChipOpen(false); //if they change the input, remove the chip for better ux
    setOwnerIdFilter(event.target.value);
  };

  // Handle changes in the property name input in filters
  const handleChangePropertyNameFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPropertyNameFilterChipOpen(false); //if they change the input, remove the chip for better ux
    setPropertyNameFilter(event.target.value);
  };

  const handleClearAllFilters = () => {
    setSelectedRegistrationStatusFilterOptions([]);
    setOwnerIdFilter('');
    setOwnerNameFilter('');
    setPropertyNameFilter('');

    fetchOwners({ StartIndex: 0, OwnerName: undefined, RegistrationStatus: undefined, OwnerExternalId: undefined, PropertyName: undefined });

    setIsOwnerNameFilterChipOpen(false);
    setIsOwnerIdFilterChipOpen(false);
    setIsPropertyNameFilterChipOpen(false);
    setIsSelectedRegistrationStatusChipOpen(false);
    setIsSelectedStatusChipOpen(false);

    handleClose();
  };

  const handleDeleteOwnerNameChip = () => {
    setOwnerNameFilter('');

    fetchOwners({ OwnerName: undefined, StartIndex: 0 });
    setIsOwnerNameFilterChipOpen(false);
    props.updateCurrentPage(0);
  };

  const handleDeleteOwnerIdChip = () => {
    setOwnerIdFilter('');

    fetchOwners({ OwnerExternalId: undefined, StartIndex: 0 });
    setIsOwnerIdFilterChipOpen(false);
    props.updateCurrentPage(0);
  };

  const handleDeletePropertyNameChip = () => {
    setPropertyNameFilter('');

    fetchOwners({ PropertyName: undefined, StartIndex: 0 });
    setIsPropertyNameFilterChipOpen(false);
    props.updateCurrentPage(0);
  };

  const handleDeleteRegistrationStatusChip = () => {
    setSelectedRegistrationStatusFilterOptions([]);

    fetchOwners({ RegistrationStatus: undefined, StartIndex: 0 });
    setIsSelectedRegistrationStatusChipOpen(false);
    props.updateCurrentPage(0);
  };

  const handleDeleteStatusChip = () => {
    setSelectedStatusFilterOptions([]);

    fetchOwners({ Status: undefined, StartIndex: 0 });
    setIsSelectedStatusChipOpen(false);
    props.updateCurrentPage(0);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center', marginBottom: '6px' }} onClick={handleClick}>
        <img src={Filter} style={{ height: '25px', width: '25px' }} alt="Filter Icon" />
        <Typography sx={{ fontFamily: 'Work Sans', fontStyle: 'normal', fontWeight: 700, marginLeft: 2 }}>Search and Filter:</Typography>
      </Box>

      <Box sx={{ marginLeft: 2, display: 'flex', flexWrap: 'wrap' }}>
        {isOwnerNameFilterChipOpen && ownerNameFilter && <OwnersTableHeaderChip label={`Name: ${ownerNameFilter}`} onDelete={handleDeleteOwnerNameChip} />}
        {isOwnerIdFilterChipOpen && ownerIdFilter && <OwnersTableHeaderChip label={`Owner ID: ${ownerIdFilter}`} onDelete={handleDeleteOwnerIdChip} />}
        {isPropertyNameFilterChipOpen && propertyNameFilter && <OwnersTableHeaderChip label={`Property: ${propertyNameFilter}`} onDelete={handleDeletePropertyNameChip} />}
        {isSelectedRegistrationStatusChipOpen && !!selectedRegistrationStatusFilterOptions.length && (
          <OwnersTableHeaderChip
            label={`Registration: ${selectedRegistrationStatusFilterOptions.map((option) => option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()).join(', ')}`}
            onDelete={handleDeleteRegistrationStatusChip}
          />
        )}
        {isSelectedStatusChipOpen && !!selectedStatusFilterOptions.length && (
          <OwnersTableHeaderChip
            label={`Status: ${selectedStatusFilterOptions.map((option) => option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()).join(', ')}`}
            onDelete={handleDeleteStatusChip}
          />
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '25px',
            padding: 2,
            width: '700px',
            ml: -2,
            mt: -2,
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6} sx={{ display: 'flex', cursor: 'pointer' }} onClick={handleClose}>
            <img src={Filter} style={{ height: '25px', width: '25px' }} alt="Filter Icon" />
            <Typography sx={{ fontFamily: 'Work Sans', fontStyle: 'normal', fontWeight: 700, marginLeft: 2 }}>Search and Filter:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button sx={{ textTransform: 'none' }} onClick={handleClearAllFilters}>
              Clear All
            </Button>
          </Grid>

          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Name:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <OwnersTableHeaderTextInput value={ownerNameFilter} onChange={handleChangeOwnerNameFilter} onEnterPress={handleSearch} autoFocus={true} />
          </Grid>

          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Owner ID:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <OwnersTableHeaderTextInput value={ownerIdFilter} onChange={handleChangeOwnerIdFilter} onEnterPress={handleSearch} />
          </Grid>

          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Property:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <OwnersTableHeaderTextInput value={propertyNameFilter} onChange={handleChangePropertyNameFilter} onEnterPress={handleSearch} />
          </Grid>

          {/* Registration Status */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Registration:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <OwnersTableHeaderMultiSelectInput
              selectedOptions={selectedRegistrationStatusFilterOptions}
              availableOptions={registrationStatusFilterOptions}
              onChange={handleChangeRegistrationStatusFilter}
            />
          </Grid>

          {/* Owner Status */}
          <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'Work Sans', fontWeight: 700 }}>Status:</Typography>
          </Grid>
          <Grid item xs={12} sm={7}>
            <OwnersTableHeaderMultiSelectInput selectedOptions={selectedStatusFilterOptions} availableOptions={statusFilterOptions} onChange={handleChangeStatusFilter} />
          </Grid>
        </Grid>

        {/* Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 15 }}>
          <Stack direction="row" spacing={2}>
            <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '24px', fontFamily: 'Work Sans !important', textTransform: 'capitalize', color: '#39A2DB', borderColor: '#39A2DB' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSearch}
              variant="contained"
              sx={{ borderRadius: '24px', fontFamily: 'Work Sans !important', textTransform: 'capitalize', color: 'white', backgroundColor: '#39A2DB', borderColor: '#39A2DB', width: '100px' }}
            >
              Search
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default OwnersFilterTableHeader;
